import React, { useMemo, useState } from 'react';
import { PlusCircleIcon, AlertTriangleIcon, ShieldCheckIcon, CheckSquareIcon, UserIcon, CheckCircleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScreenProps, MeetingAddition } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
function buildNewItems(data: any): MeetingAddition[] {
  const items: MeetingAddition[] = [];
  const steps = data.stepHazardData || [];
  const crew = data.crewRoles || [];
  const foremanName = crew.find((c: any) => c.role === 'Foreman')?.name || crew[0]?.name || 'Crew member';
  const secondCrewName = crew[1]?.name || 'Crew member';
  const midStep = steps[Math.floor(steps.length / 2)];
  if (midStep) {
    items.push({
      id: 'new-1',
      type: 'hazard',
      stepRef: `Step ${Math.floor(steps.length / 2) + 1} · ${midStep.description}`,
      title: `Changing conditions may affect this step`,
      reason: `${foremanName} noted that conditions may change during this phase and the crew should be prepared to stop work.`,
      source: crew[0]?.role || 'Foreman'
    });
  }
  const lastStep = steps[steps.length - 1];
  if (lastStep) {
    items.push({
      id: 'new-2',
      type: 'control',
      stepRef: `Step ${steps.length} · ${lastStep.description}`,
      title: `Confirm communication check before proceeding`,
      reason: `${secondCrewName} stated that clear communication must be confirmed before this step begins.`,
      source: crew[1]?.role || 'Crew member'
    });
  }
  if (steps.length > 1) {
    const secondStep = steps[1];
    items.push({
      id: 'new-3',
      type: 'control',
      stepRef: `Step 2 · ${secondStep.description}`,
      title: `Verify area is clear before starting`,
      reason: `Crew discussed ensuring the work area is clear of unauthorized personnel before proceeding.`,
      source: 'Meeting discussion'
    });
  }
  if (steps.length > 0) {
    items.push({
      id: 'new-4',
      type: 'check',
      stepRef: `Step 1 · ${steps[0].description}`,
      title: `Verify all equipment inspected before use`,
      reason: `${foremanName} reminded the crew to complete all equipment inspections before starting work.`,
      source: crew[0]?.role || 'Foreman'
    });
  }
  return items;
}
const typeConfig = {
  hazard: {
    icon: AlertTriangleIcon,
    color: 'text-red-500',
    border: 'border-l-red-500',
    label: 'New Hazard'
  },
  control: {
    icon: ShieldCheckIcon,
    color: 'text-green-500',
    border: 'border-l-green-500',
    label: 'New Control'
  },
  check: {
    icon: CheckSquareIcon,
    color: 'text-blue-500',
    border: 'border-l-blue-500',
    label: 'Safety Check'
  }
};
export function Screen15_NewInfo({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const newItems = useMemo(() => buildNewItems(data), [data]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(newItems.map(i => i.id)));
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const handleApplyUpdates = () => {
    const selectedItems = newItems.filter(item => selectedIds.has(item.id));
    if (selectedItems.length > 0) {
      updateData({
        meetingAdditions: [...(data.meetingAdditions || []), ...selectedItems]
      });
    }
    onNext();
  };
  const handleDismiss = () => {
    onNext();
  };
  return <div className="flex flex-col h-full" data-id="element-1071">
      <div className="mb-5" data-id="element-1072">
        <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-1073">
          New Information Detected
        </h2>
        <p className="text-gray-500 text-sm" data-id="element-1074">
          AI found {newItems.length} items from the meeting. Select which to add
          to the PTP.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-3" data-id="element-1075">
        {newItems.map((item, i) => {
        const config = typeConfig[item.type];
        const Icon = config.icon;
        const isSelected = selectedIds.has(item.id);
        return <motion.div key={item.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.08
        }} onClick={() => toggleSelection(item.id)} className={`bg-white border rounded-xl p-4 border-l-4 cursor-pointer transition-all ${isSelected ? `border-gray-200 ${config.border}` : 'border-gray-200 border-l-gray-300 opacity-60'}`} data-id="element-1076">
              <div className="flex items-start gap-3" data-id="element-1077">
                <div className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 text-transparent'}`} data-id="element-1078">
                  <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1079" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1080">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1081">
                    <Icon className={`w-3.5 h-3.5 ${config.color}`} data-id="element-1082" />
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1083">
                      {config.label}
                    </span>
                    <span className="text-gray-400 text-[10px]" data-id="element-1084">·</span>
                    <span className="text-[10px] text-gray-400" data-id="element-1085">
                      {item.stepRef}
                    </span>
                  </div>
                  <p className={`font-medium text-sm mb-2 ${isSelected ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-1086">
                    {item.title}
                  </p>
                  <div className="bg-gray-50 rounded-lg p-2.5" data-id="element-1087">
                    <div className="flex items-start gap-2" data-id="element-1088">
                      <UserIcon className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1089" />
                      <div data-id="element-1090">
                        <p className="text-gray-500 text-xs leading-snug italic" data-id="element-1091">
                          "{item.reason}"
                        </p>
                        <p className="text-gray-400 text-[10px] mt-1" data-id="element-1092">
                          — {item.source}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>;
      })}

        {selectedIds.size > 0 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-center justify-center gap-2" data-id="element-1093">
            <PlusCircleIcon className="w-4 h-4 text-yellow-500" data-id="element-1094" />
            <span className="text-yellow-600 font-bold text-sm" data-id="element-1095">
              {selectedIds.size} update{selectedIds.size !== 1 ? 's' : ''} will
              be added to the PTP
            </span>
          </motion.div>}
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-2 bg-gray-50" data-id="element-1096">
        <PrimaryButton onClick={handleApplyUpdates} disabled={selectedIds.size === 0} data-id="element-1097">
          Add {selectedIds.size} Selected to PTP
        </PrimaryButton>
        <SecondaryButton onClick={handleDismiss} data-id="element-1098">Skip</SecondaryButton>
      </div>
    </div>;
}